/* eslint-disable max-lines */
import React, { createContext, useContext, useReducer } from 'react';
import { ReactEditor } from 'slate-react';
import { Editor, Node, Text, Transforms } from 'slate';
import { Range } from 'slate/dist/interfaces/range';
import { HistoryEditor } from 'slate-history';

interface EditorChange {
  editor: ReactEditor;
  timestamp: number;
  undone: boolean;
}

interface EditorState {
  editors: Record<string, ReactEditor | null>;
  editorChanges: EditorChange[];
  currentChangeIndex: number;
}

type EditorAction =
  | { type: 'ADD_EDITOR'; payload: { id: string; editor: ReactEditor } }
  | { type: 'REMOVE_EDITOR'; payload: { id: string } }
  | { type: 'ADD_EDITOR_CHANGE'; payload: { editor: ReactEditor } }
  | { type: 'MARK_CHANGE_UNDONE'; payload: { index: number } }
  | { type: 'MARK_CHANGE_REDONE'; payload: { index: number } }
  | { type: 'UPDATE_CURRENT_INDEX'; payload: { index: number } };

const initialState: EditorState = {
  editors: {},
  editorChanges: [],
  currentChangeIndex: -1
};

// Limit editor changes to prevent memory leaks (see docs/MEMORY_OPTIMIZATION.md)
// Each change stores a ReactEditor reference which can be large
const MAX_EDITOR_CHANGES = 50;

function editorReducer(state: EditorState, action: EditorAction): EditorState {
  switch (action.type) {
    case 'ADD_EDITOR':
      return {
        ...state,
        editors: {
          ...state.editors,
          [action.payload.id]: action.payload.editor
        }
      };
    case 'REMOVE_EDITOR': {
      const updatedEditors = { ...state.editors };
      delete updatedEditors[action.payload.id];
      return { ...state, editors: updatedEditors };
    }
    case 'ADD_EDITOR_CHANGE': {
      const TIME_THRESHOLD = 2000;
      if (
        state.editorChanges.length === 0 ||
        state.editorChanges[0].editor !== action.payload.editor ||
        Date.now() - state.editorChanges[0].timestamp > TIME_THRESHOLD
      ) {
        const newChanges = [
          {
            editor: action.payload.editor,
            timestamp: Date.now(),
            undone: false
          },
          ...state.editorChanges
        ];

        // Trim to MAX_EDITOR_CHANGES to prevent unbounded growth
        const trimmedChanges =
          newChanges.length > MAX_EDITOR_CHANGES
            ? newChanges.slice(0, MAX_EDITOR_CHANGES)
            : newChanges;

        return {
          ...state,
          editorChanges: trimmedChanges,
          currentChangeIndex: state.currentChangeIndex + 1
        };
      }
      return state;
    }
    case 'MARK_CHANGE_UNDONE':
      return {
        ...state,
        editorChanges: state.editorChanges.map((change, index) =>
          index === action.payload.index ? { ...change, undone: true } : change
        )
      };
    case 'MARK_CHANGE_REDONE':
      return {
        ...state,
        editorChanges: state.editorChanges.map((change, index) =>
          index === action.payload.index ? { ...change, undone: false } : change
        )
      };
    case 'UPDATE_CURRENT_INDEX':
      return { ...state, currentChangeIndex: action.payload.index };
    default:
      return state;
  }
}

const CaptionEditorContext = createContext<{
  editors: Record<string, ReactEditor | null>;
  addEditor: (id: string, editor: ReactEditor) => void;
  removeEditor: (id: string) => void;
  undo: () => void;
  redo: () => void;
  trackModification: (editor: ReactEditor) => void;
  replaceAllWordsInEditor: (
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    replacement?: string
  ) => void;
  /**
   * Replace exactly one occurrence identified by the segment's
   * pre-replace text and the match's char offsets. Scans every mounted
   * editor, finds the one whose first text node matches `segmentText`,
   * and runs a targeted `Transforms.insertText` at [from, to]. Used by
   * the Find & Replace popover's single-match "Replace" button
   * (#2231 reopen — Replace All was the only path, so stepping through
   * matches was useless).
   */
  replaceOneInEditor: (
    segmentText: string,
    from: number,
    to: number,
    replacement: string
  ) => void;
  isAnyEditorFocused: boolean;
  canUndoText: boolean;
  canRedoText: boolean;
}>({
  editors: {},
  addEditor: () => {},
  removeEditor: () => {},
  replaceAllWordsInEditor: () => {},
  replaceOneInEditor: () => {},
  undo: () => {},
  redo: () => {},
  trackModification: () => {},
  isAnyEditorFocused: false,
  canUndoText: false,
  canRedoText: false
});

export const CaptionEditorProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(editorReducer, initialState);

  const addEditor = (id: string, editor: ReactEditor) => {
    dispatch({ type: 'ADD_EDITOR', payload: { id: `editor-${id}`, editor } });
  };

  const removeEditor = (id: string) => {
    dispatch({ type: 'REMOVE_EDITOR', payload: { id: `editor-${id}` } });
  };

  const escapeRegex = (str: string) => {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const findMatches = (
    editor: Editor,
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean
  ) => {
    if (!searchText) return [];

    try {
      const matches: Range[] = [];
      const flags = caseSensitive ? 'g' : 'gi';
      const escapedText = escapeRegex(searchText);
      const searchQuery = wholeWord ? `\\b${escapedText}\\b` : escapedText;
      const regex = new RegExp(searchQuery, flags);

      for (const [node, path] of Node.texts(editor)) {
        if (Text.isText(node)) {
          const text = node.text;
          let match;
          while ((match = regex.exec(text)) !== null) {
            const start = match.index;
            const end = start + match[0].length;
            matches.push({
              anchor: { path, offset: start },
              focus: { path, offset: end }
            });
          }
        }
      }
      return matches;
    } catch (error) {
      // Silently handle regex or Slate errors
      // Error is caught and ignored intentionally
      return [];
    }
  };

  const handleReplaceAll = (
    editor: Editor,
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    replacement?: string
  ) => {
    try {
      const matches = findMatches(editor, searchText, caseSensitive, wholeWord);
      if (matches.length === 0) return;
      // Reverse iteration prevents path invalidation when replacing text
      Editor.withoutNormalizing(editor, () => {
        for (let i = matches.length - 1; i >= 0; i--) {
          const match = matches[i];
          Transforms.select(editor, match);
          Transforms.insertText(editor, replacement || '');
        }
      });
    } catch (error) {
      // Silently handle Slate DOM resolution errors
      // Error is caught and ignored intentionally
    }
  };

  const replaceAllWordsInEditor = (
    searchText: string,
    caseSensitive: boolean,
    wholeWord: boolean,
    replacement?: string
  ) => {
    for (const editor of Object.values(state.editors)) {
      if (editor) {
        handleReplaceAll(
          editor,
          searchText,
          caseSensitive,
          wholeWord,
          replacement
        );
      }
    }
  };

  const replaceOneInEditor = (
    segmentText: string,
    from: number,
    to: number,
    replacement: string
  ) => {
    try {
      for (const editor of Object.values(state.editors)) {
        if (!editor) continue;
        // Each caption segment is its own Slate editor with a single
        // text leaf, so matching the first text node's content against
        // the segment's current text uniquely identifies the editor
        // the match belongs to.
        const firstText = Node.string(editor);
        if (firstText !== segmentText) continue;
        for (const [node, path] of Node.texts(editor)) {
          if (!Text.isText(node)) continue;
          if (node.text !== segmentText) continue;
          Transforms.select(editor, {
            anchor: { path, offset: from },
            focus: { path, offset: to }
          });
          Transforms.insertText(editor, replacement);
          return;
        }
      }
    } catch {
      // Silently handle Slate DOM resolution errors — mirrors the
      // replace-all helper's fault tolerance.
    }
  };

  const trackModification = (editor: ReactEditor) => {
    dispatch({ type: 'ADD_EDITOR_CHANGE', payload: { editor } });
  };

  const getFocusedEditor = (): ReactEditor | null => {
    for (const editor of Object.values(state.editors)) {
      if (editor && ReactEditor.isFocused(editor)) {
        return editor;
      }
    }
    return null;
  };

  const hasSlateUndo = (editor: ReactEditor | null) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    !!(editor && (editor as any).history?.undos?.length > 0);

  const hasSlateRedo = (editor: ReactEditor | null) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    !!(editor && (editor as any).history?.redos?.length > 0);

  const handleUndo = () => {
    const focused = getFocusedEditor();
    if (
      focused &&
      HistoryEditor.isHistoryEditor(focused as unknown as HistoryEditor)
    ) {
      if (hasSlateUndo(focused)) {
        ReactEditor.focus(focused);
        HistoryEditor.undo(focused as unknown as HistoryEditor);
        return;
      }
    }

    const changes = state.editorChanges;
    if (changes.length === 0) return;
    for (let i = 0; i < changes.length; i++) {
      const change = changes[i];
      if (!change.undone) {
        const editor = change.editor;
        if (HistoryEditor.isHistoryEditor(editor as unknown as HistoryEditor)) {
          ReactEditor.focus(editor);
          HistoryEditor.undo(editor as unknown as HistoryEditor);
          dispatch({ type: 'MARK_CHANGE_UNDONE', payload: { index: i } });
          dispatch({ type: 'UPDATE_CURRENT_INDEX', payload: { index: i } });
        }
        break;
      }
    }
  };

  const handleRedo = () => {
    const focused = getFocusedEditor();
    if (
      focused &&
      HistoryEditor.isHistoryEditor(focused as unknown as HistoryEditor)
    ) {
      if (hasSlateRedo(focused)) {
        ReactEditor.focus(focused);
        HistoryEditor.redo(focused as unknown as HistoryEditor);
        return;
      }
    }

    const changes = state.editorChanges;
    if (changes.length === 0) return;
    for (let i = 0; i < changes.length; i++) {
      const change = changes[i];
      if (change.undone) {
        const editor = change.editor;
        if (HistoryEditor.isHistoryEditor(editor as unknown as HistoryEditor)) {
          ReactEditor.focus(editor);
          HistoryEditor.redo(editor as unknown as HistoryEditor);
          dispatch({ type: 'MARK_CHANGE_REDONE', payload: { index: i } });
          dispatch({ type: 'UPDATE_CURRENT_INDEX', payload: { index: i } });
        }
        break;
      }
    }
  };

  const focusedEditor = getFocusedEditor();

  return (
    <CaptionEditorContext.Provider
      value={{
        editors: state.editors,
        addEditor,
        removeEditor,
        replaceAllWordsInEditor,
        replaceOneInEditor,
        undo: handleUndo,
        redo: handleRedo,
        trackModification,
        // convenience flags for UI
        isAnyEditorFocused: !!focusedEditor,
        canUndoText: hasSlateUndo(focusedEditor),
        canRedoText: hasSlateRedo(focusedEditor)
      }}
    >
      {children}
    </CaptionEditorContext.Provider>
  );
};

export const useCaptionEditorContext = () => useContext(CaptionEditorContext);
